import { camelCase, upperFirst } from 'lodash';
import React, { useEffect, useMemo, useState } from "react";
import axiosWithAuth from "../../helpers/AxiosHelper";
import errorMessage from "../../helpers/errorMessage";
import { useAuth } from "../../hooks/useAuth";
import PlatformsOverview from "../platforms/Overview/platformOverview";
import { currencyValues, renderPerCurrency, sumByCurrency } from "../platforms/Overview/platformOverviewColumns";
import { PlatformOverviewDetailBody } from "../platforms/Overview/PlatformOverviewDetailBody";
import PageHeader from "../common/PageHeader";
import "./data.css";

// Mirrors PlatformOverviewDetails.js's own :platformName URL-slug matching
// (SUBSTITUTE(...) in backend/api/overview.js) so this filter agrees with
// what the backend itself considers "the same platform".
const normalizePlatformKey = (s) => String(s || "").toLowerCase().replaceAll("-", "").replaceAll("_", "");

// Same tile shape used elsewhere (PlatformReport.js's KpiTile,
// PlatformOverviewDetailBody.js's StatTile - surface-card/shadow-1/
// border-round, all theme tokens, not hardcoded colors), plus an icon badge
// in --primary-color so this page's own brand-new tiles still read as part
// of the same design language rather than a one-off.
// KPI tile - see ui-kit.css's .ex-stat. min-width:0 on the body lets a long
// label/number wrap inside the tile instead of widening the whole grid.
const SummaryTile = ({ icon, label, value }) => (
  <div className="ex-stat">
    <span className="ex-stat-icon"><i className={`pi ${icon}`} /></span>
    <div className="ex-stat-body">
      <div className="ex-stat-label">{label}</div>
      <div className="ex-stat-value">{value}</div>
    </div>
  </div>
);

const OriginatorsData = ({ setLoading = () => {} }) => {
  const { platform } = useAuth();
  const isExaloanMember = !platform || platform.toLowerCase() === "exaloan";
  const [platformOverviewData, setPlatformOverviewData] = useState(null);

  useEffect(() => {
    setLoading(true);
    axiosWithAuth
      .get("/overview")
      .then((res) => {
        const rows = res.data || [];
        // Backend already scopes a real external account's own token to
        // just its own platform row - this extra filter is what makes
        // "View As" (an Exaloan admin previewing another platform, purely
        // client-side state - see AuthProvider.js) behave the same way,
        // since the real token behind that request is still the admin's own.
        const scoped = isExaloanMember
          ? rows
          : rows.filter((row) => normalizePlatformKey(row.platform) === normalizePlatformKey(platform));
        setPlatformOverviewData(scoped);
      })
      .catch((err) => console.error('Overview fetch failed:', errorMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  // Exaloan-only - a platform account's own row is already broken down in
  // full by PlatformOverviewDetailBody below, so rolling it up into a single
  // number here would just be showing the same figure twice.
  const aggregate = useMemo(() => {
    if (!isExaloanMember || !platformOverviewData) return null;
    return {
      platformCount: platformOverviewData.length,
      totalLoanCount: platformOverviewData.reduce((acc, row) => acc + (row.total_loan_count || 0), 0),
      // Platforms report in different currencies - one total per currency.
      totalOriginationVolume: sumByCurrency(platformOverviewData.map((row) => currencyValues(row, "origination_volume"))),
    };
  }, [isExaloanMember, platformOverviewData]);

  return (
    <div className="content-container">
      {/* Exaloan staff already land on the full multi-platform table below,
          so a generic "Dashboard" heading is enough context there. A
          platform account only ever sees its own single row/detail though,
          which read as bare without something naming who "your data" belongs
          to - a personalized greeting instead of the same generic heading. */}
      {/* The daily-refresh note used to be its own full-width line below
          this - folded into the same line as the subtitle instead so it
          doesn't take up a whole row of its own just for a few words.
          platform_overview (the collection GET /overview reads) is
          recomputed by a separate scheduled Lambda once a day on weekdays,
          not on every page load - see the "platform_overview" Bitbucket
          repo's template.yaml (cron(00 22 ? * MON-FRI *), 8pm German time). */}
      {isExaloanMember
        ? (
          <PageHeader
            icon="pi-th-large"
            title="Dashboard"
            subtitle="A quick look at every Originator Exaloan works with - data is refreshed once daily."
          />
        )
        : (
          <PageHeader
            icon="pi-home"
            title={`Welcome, ${upperFirst(camelCase(platform))}`}
            subtitle="Here's an overview of your data with Exaloan - refreshed once daily."
          />
        )}

      {aggregate && (
        <div className="ex-stat-grid">
          <SummaryTile icon="pi-building" label="Originators" value={aggregate.platformCount} />
          <SummaryTile icon="pi-list" label="Total Loan Count" value={aggregate.totalLoanCount.toLocaleString('en-us')} />
          <SummaryTile icon="pi-wallet" label="Total Origination Volume" value={renderPerCurrency(aggregate.totalOriginationVolume)} />
        </div>
      )}

      {/* The summary table is only meaningful when there's more than one
          row to compare - a non-Exaloan account only ever has its own
          single row, so skip straight to its full breakdown instead of
          showing a one-row table above it. */}
      {/* Not wrapped in a Panel (unlike the tile row above) - this table
          already has ~17 columns and needs every bit of width it can get;
          a Panel's own content padding was enough to push it into a
          horizontal scrollbar it didn't need before, just to show the exact
          same columns. */}
      {isExaloanMember && platformOverviewData && (
        <PlatformsOverview platformOverview={platformOverviewData} linkable={isExaloanMember} />
      )}
      {!isExaloanMember && platformOverviewData?.length > 0 && (
        <PlatformOverviewDetailBody platformName={platform} setLoading={setLoading} />
      )}
    </div>
  );
};

export default OriginatorsData;
